import { TraceEvent } from 'src/app/model/response/ai-chat-response';

/*
 * Formatação do painel de trace (ADM, @debug): resumo da linha fechada e campos do
 * detalhe aberto. Eventos v2 (já gravados no histórico) não trazem os campos novos do
 * runtime v3 — por isso todo campo opcional é tratado como ausente, nunca como zero.
 */

/** Uma linha do detalhe. `sub` desce um nível (camadas do prompt, trechos do RAG). */
export interface TraceField {
  label: string;
  value: string;
  /** Destaque de alerta depois do valor (cache quebrado, corte, motivo de descarte). */
  warn?: string;
  /** Item gravado no backend com o texto integral: habilita o "ver texto". */
  ref?: string;
  sub?: TraceField[];
}

export function formatTraceTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;
}

function shortModel(model: string | undefined): string {
  return (model ?? '?').replace(/^us\./, '').replace(/^(anthropic|amazon)\./, '').replace(/-v1:0$/, '');
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : String(JSON.stringify(value));
}

/** Junta as partes presentes; `false`/`0`/`''`/nulos (campo ausente) somem. */
function joinParts(parts: unknown[]): string {
  return parts.filter(Boolean).join(' · ');
}

export function getTraceIcon(type: string): string {
  switch (type) {
    case 'node': return 'pi-sitemap';
    case 'route': return 'pi-arrow-right-arrow-left';
    case 'agent': return 'pi-user';
    case 'tool': return 'pi-wrench';
    case 'guardrail': return 'pi-shield';
    case 'usage': return 'pi-chart-bar';
    case 'llm_call': return 'pi-microchip-ai';
    case 'rag_rewrite': return 'pi-pencil';
    case 'rag': return 'pi-search';
    case 'intent': return 'pi-directions';
    default: return 'pi-circle';
  }
}

export function getTraceSummary(event: TraceEvent): string {
  switch (event.type) {
    case 'node':
      return `${event.node ?? '?'}${event.elapsed_ms != null ? ` · ${event.elapsed_ms}ms` : ''}`;
    case 'route':
      return `${event.from ?? '?'} → ${event.to ?? '?'}`;
    case 'agent':
      return `${event.agent ?? '?'}`;
    case 'tool': {
      const scope = event.scope ? ` (${event.scope})` : '';
      const duration = event.duration_ms != null ? ` · ${event.duration_ms}ms` : '';
      const status = event.status === 'error' ? ' ✗' : event.status === 'ok' ? ' ✓' : '';
      return joinParts([
        `${event.tool_name ?? '?'}${scope}${duration}${status}`,
        event.result_chars != null && `${formatTraceTokens(event.result_chars)} chars`,
        event.closing && `fechamento ${event.closing}`,
        event.truncated_by && event.truncated_by !== 'none' && `cortado ${event.truncated_by}`,
      ]);
    }
    case 'guardrail': {
      const stage = event.stage ? ` (${event.stage})` : '';
      return `${event.node ?? '?'}${stage} · ${event.blocked ? 'bloqueado' : 'ok'}`;
    }
    case 'usage': {
      const inTok = event.input_tokens != null ? formatTraceTokens(event.input_tokens) : '?';
      const outTok = event.output_tokens != null ? formatTraceTokens(event.output_tokens) : '?';
      return `${event.model ?? '?'} · ${inTok} in / ${outTok} out`;
    }
    case 'intent':
      return joinParts([
        `${event.layer ?? '?'} ${event.extra?.source ?? event.rule ?? '?'} → ${event.agent ?? '?'}`,
        event.tools_available != null && `${event.tools_available} tools`,
      ]);
    case 'rag_rewrite':
      return joinParts([
        'rewrite',
        shortModel(event.model),
        event.duration_ms != null && `${event.duration_ms}ms`,
        `${(event.manuais ?? []).length} MANUAIS / ${(event.suporte ?? []).length} SUPORTE`,
        `veículo ${event.veiculo === 'sim' ? 'sim' : 'não'}`,
        event.tipo,
        event.src && event.src !== 'rewrite' && event.src,
      ]);
    case 'rag': {
      const firstQuery = typeof event.query === 'string' ? event.query.split(' | ')[0] : '';
      return joinParts([
        `rag ${event.source ?? '?'}`,
        `${event.kept ?? '?'}${event.candidates != null ? ` de ${event.candidates}` : ''}`,
        Array.isArray(event.dropped) && `${event.dropped.length} descartados`,
        firstQuery && `"${firstQuery}"`,
      ]);
    }
    case 'llm_call':
      return llmCallSummary(event);
    case 'summary':
      return joinParts([
        event.total_ms != null && `${(event.total_ms / 1000).toFixed(1)}s`,
        event.tool_calls != null && `${event.tool_calls} tool`,
        event.rag_chunks_kept != null && `${event.rag_chunks_kept} trechos`,
        typeof event.cost_usd_total === 'number' && `$${event.cost_usd_total.toFixed(4)}`,
      ]) || 'summary';
    case 'meta':
      return event.trace_truncated ? 'trace cortado no limite de gravação' : 'meta';
    default:
      return event.type ?? 'evento';
  }
}

function llmCallSummary(event: TraceEvent): string {
  const layers: any[] = event.layers ?? [];
  const totalChars = layers.reduce((sum, l) => sum + (l.chars ?? 0), 0);
  const prefixChars = layers[0]?.name === 'prefixo_estatico' ? layers[0].chars : 0;
  const cache = event.cache?.supported
    ? (event.cache.broke ? 'cache QUEBROU' : event.cache_read_tokens ? 'cache hit' : null)
    : null;
  const output = event.output?.kind === 'tool_use'
    ? `→ ${event.output.tool}`
    : event.output ? `→ texto ${formatTraceTokens(event.output.chars ?? 0)} chars` : '';
  const dropped = event.cuts?.history_messages_dropped;
  return joinParts([
    `llm #${event.seq ?? '?'} ${event.agent ?? '?'}`,
    shortModel(event.model),
    event.duration_ms != null && `${(event.duration_ms / 1000).toFixed(1)}s`,
    `${formatTraceTokens(totalChars)} chars${prefixChars ? ` (${formatTraceTokens(prefixChars)} prefixo)` : ''}`,
    cache,
    `${layers.length} camadas`,
    `${event.tools?.count ?? 0} tools`,
    dropped && `corte ${dropped} msgs`,
  ]) + (output ? ` ${output}` : '');
}

export function getTraceFields(event: TraceEvent): TraceField[] {
  switch (event.type) {
    case 'llm_call': return llmCallFields(event);
    case 'rag_rewrite': return ragRewriteFields(event);
    case 'rag': return ragFields(event);
    case 'intent': return intentFields(event);
    case 'tool': return toolFields(event);
    default:
      return Object.keys(event)
        .filter((key) => key !== 'type')
        .map((key) => ({ label: key, value: asText(event[key]) }));
  }
}

function llmCallFields(event: TraceEvent): TraceField[] {
  const fields: TraceField[] = [
    { label: 'modelo', value: joinParts([event.model, event.duration_ms != null && `${event.duration_ms}ms`, event.stop_reason]) },
    {
      label: 'tokens',
      value: `${event.input_tokens ?? '?'} in / ${event.output_tokens ?? '?'} out · cache ${event.cache_read_tokens ?? 0} lidos`,
    },
  ];
  const cache = event.cache;
  if (cache) {
    const changed: string[] = cache.changed_layers ?? [];
    fields.push({
      label: 'cache',
      value: joinParts([
        cache.supported ? (!cache.broke && 'hit') : 'modelo sem cache',
        `prefixo ${cache.prefix_hash ?? '-'}`,
        changed.length ? `mudou: ${changed.join(', ')}` : 'nenhuma camada mudou',
      ]),
      warn: cache.broke ? 'quebrou' : undefined,
    });
  }
  const cuts = event.cuts;
  if (cuts) {
    const kept: string[] = cuts.never_cut_kept ?? [];
    fields.push({
      label: 'cortes',
      value: joinParts([
        `${cuts.history_messages_dropped ?? 0} msgs / ${formatTraceTokens(cuts.history_chars_dropped ?? 0)} chars do histórico`,
        kept.length && `poupados: ${kept.join(', ')}`,
      ]),
    });
  }
  const tools = event.tools;
  if (tools) {
    const names: string[] = tools.names ?? [];
    fields.push({ label: 'tools', value: `${tools.count ?? 0}${names.length ? `: ${names.join(', ')}` : ''}`, ref: tools.ref });
  }
  fields.push({
    label: 'camadas',
    value: '',
    sub: (event.layers ?? []).map((layer: any) => {
      const parts = Object.entries<number>(layer.parts ?? {})
        .filter(([, chars]) => chars)
        .map(([name, chars]) => `${name} ${formatTraceTokens(chars)}`);
      return {
        label: layer.name ?? '?',
        value: `${layer.messages != null ? `${layer.messages} msgs · ` : ''}${formatTraceTokens(layer.chars ?? 0)} chars${parts.length ? ` (${parts.join(', ')})` : ''}`,
        ref: layer.ref,
      };
    }),
  });
  const output = event.output;
  if (output) {
    fields.push({
      label: 'saída',
      value: output.kind === 'tool_use' ? `tool_use → ${output.tool}` : `texto · ${formatTraceTokens(output.chars ?? 0)} chars`,
      ref: output.ref,
    });
  }
  return fields;
}

function ragRewriteFields(event: TraceEvent): TraceField[] {
  return [
    { label: 'original', value: asText(event.original) },
    ...(event.manuais ?? []).map((q: string, i: number) => ({ label: `MANUAIS ${i + 1}`, value: q })),
    ...(event.suporte ?? []).map((q: string, i: number) => ({ label: `SUPORTE ${i + 1}`, value: q })),
    { label: 'veículo / tipo', value: `${event.veiculo ?? '?'} / ${event.tipo ?? '?'}` },
    { label: 'origem', value: `${event.src ?? '?'}${event.fail_open ? ' (fail-open: buscou com a pergunta crua)' : ''}` },
    { label: 'modelo', value: joinParts([event.model, event.duration_ms != null && `${event.duration_ms}ms`]) },
  ];
}

function ragFields(event: TraceEvent): TraceField[] {
  const scores: number[] = event.rerank_scores ?? [];
  const fields: TraceField[] = [
    { label: 'query', value: asText(event.query) },
    {
      label: 'resultado',
      value: joinParts([
        `${event.kept ?? '?'} mantidos${event.candidates != null ? ` de ${event.candidates} candidatos` : ''}`,
        event.rerank && scores.length && `rerank ${scores.join(', ')}`,
      ]),
      ref: event.ref,
    },
    {
      label: 'mantidos',
      value: '',
      sub: (event.chunks ?? []).map((c: any) => ({ label: scoreText(c.score), value: c.title ?? '?' })),
    },
  ];
  if (Array.isArray(event.dropped)) {
    fields.push({
      label: 'descartados',
      value: '',
      sub: event.dropped.map((c: any) => ({ label: scoreText(c.score), value: c.title ?? '?', warn: c.reason })),
    });
  }
  return fields;
}

function scoreText(score: unknown): string {
  return typeof score === 'number' ? score.toFixed(2) : '-';
}

function intentFields(event: TraceEvent): TraceField[] {
  const fields: TraceField[] = [
    { label: 'camada', value: joinParts([event.layer, event.rule]) },
    { label: 'agente', value: joinParts([event.agent, event.confidence && `confiança ${event.confidence}`]) },
    { label: 'tools disponíveis', value: `${event.tools_available ?? '?'}` },
  ];
  if (event.extra) fields.push({ label: 'extra', value: asText(event.extra) });
  return fields;
}

function toolFields(event: TraceEvent): TraceField[] {
  const fields: TraceField[] = [
    { label: 'argumentos', value: asText(event.arguments) },
    {
      label: 'resultado',
      value: joinParts([event.result_chars != null && `${formatTraceTokens(event.result_chars)} chars`, event.status]),
      warn: event.truncated_by && event.truncated_by !== 'none' ? `cortado por ${event.truncated_by}` : undefined,
      ref: event.result_ref,
    },
  ];
  // Sem item gravado (evento v2), o resumo de 500 chars é tudo o que há do resultado.
  if (!event.result_ref && event.result_summary) fields.push({ label: 'resumo', value: asText(event.result_summary) });
  if (event.closing) fields.push({ label: 'fechamento', value: event.closing });
  if (event.duration_ms != null) fields.push({ label: 'duração', value: `${event.duration_ms}ms` });
  return fields;
}
